import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiClient } from '@/lib/apiClient'
import { API_ROUTES } from '@/constants/apiRoutes'
import type { AuthState, VerifyState } from '@/types'

export const getAuth = createAsyncThunk<
  { access_token: string; refresh_token: string },
  { code: string | null; redirectUri: string },
  { rejectValue: string }
>(
  API_ROUTES.CALLBACK_ENDPOINT,
  async ({ code, redirectUri }, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<{ access_token: string; refresh_token: string }>(
        API_ROUTES.CALLBACK_ENDPOINT,
        {
          params: {
            code,
            redirect_uri: redirectUri,
          },
        }
      )
      return res
    } catch (error: any) {
      const detail = error?.response?.data?.detail
      if (typeof detail === 'string') {
        return rejectWithValue(detail)
      }
      if (detail?.message || detail?.error) {
        return rejectWithValue(
          [detail.message, detail.error].filter(Boolean).join(': ')
        )
      }
      return rejectWithValue(error?.message || 'getAuth failed')
    }
  }
)

export const verifyToken = createAsyncThunk<
  VerifyState,
  string,
  { rejectValue: string }
>(API_ROUTES.VERIFY_TOKEN_ENDPOINT, async (token, { rejectWithValue }) => {
  try {
    const res = await apiClient.get<VerifyState>(
      `${API_ROUTES.VERIFY_TOKEN_ENDPOINT}?token=${token}`
    )
    return res
  } catch (error: any) {
    const detail = error?.response?.data?.detail
    if (typeof detail === 'string') {
      return rejectWithValue(detail)
    }
    if (detail?.message || detail?.error) {
      return rejectWithValue(
        [detail.message, detail.error].filter(Boolean).join(': ')
      )
    }
    return rejectWithValue(error?.message || 'verifyToken failed')
  }
})

const initialState: AuthState = {
  user: null,
  access_token: '',
  refresh_token: null,
  loading: false,
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAuth.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAuth.fulfilled, (state, action) => {
        state.access_token = action.payload.access_token
        state.refresh_token = action.payload.refresh_token
        state.loading = false
        state.error = null
      })
      .addCase(getAuth.rejected, (state, action) => {
        state.error =
          (action.payload as string) || action.error.message || 'getAuth failed'
        state.loading = false
      })

      .addCase(verifyToken.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.user = action.payload.payload
        state.loading = false
        state.error = null
      })
      .addCase(verifyToken.rejected, (state, action) => {
        state.error =
          (action.payload as string) || action.error.message || 'verifyToken failed'
        state.loading = false
      })
  },
})

export default authSlice.reducer